// ============================================================
// store/snap-stash/createSnapStashStore.ts
//
// The FR-W6 flow state machine. All I/O goes through injected
// deps (service / normalize / getLocation) so the machine is
// fully unit-testable; the context provider supplies the real
// implementations. Service errors arrive as values, never
// throws — every transition is explicit here.
// ============================================================

import { create } from 'zustand';
import type {
  ImageExtractionCreateRequest,
  ImageExtractionCommitRequest,
  LocationHint,
  StashCategory,
} from '@stash/shared-types';
import type { ImageExtractionService } from '../../services/image-extraction-service';
import type { NormalizeResult } from '../../utils/image-normalize';
import type { SnapStashFlow, SnapStashStore } from './types';

export interface SnapStashDeps {
  service: Pick<ImageExtractionService, 'createExtraction' | 'enrich' | 'commit' | 'getSnapshot'>;
  normalize(file: File): Promise<NormalizeResult>;
  getLocation(): Promise<LocationHint | undefined>;
}

const SLOW_TURN_MS = 3000;
const EXPIRED_NOTICE = 'That session expired — let’s try again with a fresh photo.';

type CommitTurnRequest = Omit<ImageExtractionCommitRequest, 'idempotencyKey'>;

type RetryTurn =
  | { turn: 'create'; file: File }
  | { turn: 'enrich' }
  // The full commit turn, so retry() can re-issue it without re-entering
  // save()/resolveMatch() — their step guards no longer hold once fail()
  // has moved the flow to 'error'.
  | { turn: 'commit'; sessionId: string; request: CommitTurnRequest };

export function createSnapStashStore(deps: SnapStashDeps) {
  // Non-reactive turn bookkeeping (not rendered, so kept off the store).
  let retryFrom: RetryTurn | null = null;
  let slowTimer: ReturnType<typeof setTimeout> | undefined;
  // Exact phase-1 vendor payload, resent verbatim on phase 2 (authoritative).
  let lastVendorPayload: Record<string, unknown> | null = null;

  return create<SnapStashStore>((set, get) => {
    const beginTurn = () => {
      clearTimeout(slowTimer);
      set({ slowTurn: false });
      slowTimer = setTimeout(() => set({ slowTurn: true }), SLOW_TURN_MS);
    };
    const endTurn = () => {
      clearTimeout(slowTimer);
      set({ slowTurn: false });
    };

    /** Route a service error: expiry restarts the flow; the rest render. */
    const fail = (error: { kind: string } & Record<string, unknown>): void => {
      endTurn();
      if (error.kind === 'sessionExpired') {
        get().reset();
        set({ restartNotice: EXPIRED_NOTICE });
        return;
      }
      if (error.kind === 'alreadyCommitted') {
        const body = error.body as { vendorId: string; stashCardId: string };
        set({
          flow: {
            step: 'done', vendorId: body.vendorId, stashCardId: body.stashCardId, createdVendor: false,
          },
        });
        return;
      }
      set({ flow: { step: 'error', error } as SnapStashFlow });
    };

    return {
      flow: { step: 'idle' },
      sessionId: null,
      expiresAt: null,
      chosenCandidateId: null,
      editedFields: {},
      selectedCategories: [],
      photoPreviewUrl: null,
      slowTurn: false,
      restartNotice: null,

      async startWithFile(file) {
        set({ flow: { step: 'uploading' }, restartNotice: null });
        const normalized = await deps.normalize(file);
        if (!normalized.ok) {
          const kind = normalized.error === 'payloadTooLarge' ? 'payloadTooLarge' : 'unsupportedImage';
          set({ flow: { step: 'error', error: { kind } } });
          return;
        }
        set({
          photoPreviewUrl: `data:${normalized.image.mimeType};base64,${normalized.image.imageData}`,
        });
        const locationHint = await deps.getLocation();
        const request: ImageExtractionCreateRequest = {
          ...normalized.image,
          ...(locationHint ? { locationHint } : {}),
        };
        retryFrom = { turn: 'create', file };
        beginTurn();
        const result = await deps.service.createExtraction(request);
        endTurn();
        if (!result.ok) { fail(result.error); return; }

        const { sessionId, expiresAt, sourceType, candidates, observed } = result.data;
        set({ sessionId, expiresAt });
        if (candidates.length === 1) {
          set({
            chosenCandidateId: candidates[0].candidateId,
            flow: { step: 'confirming', candidate: candidates[0] },
          });
        } else {
          set({ flow: { step: 'choosing', candidates, observed, sourceType } });
        }
      },

      chooseCandidate(candidateId) {
        const flow = get().flow;
        if (flow.step !== 'choosing') return;
        const candidate = flow.candidates.find((entry) => entry.candidateId === candidateId);
        if (!candidate) return;
        set({ chosenCandidateId: candidateId, flow: { step: 'confirming', candidate } });
      },

      setField(field, value) {
        set({ editedFields: { ...get().editedFields, [field]: value } });
      },

      async confirmAndEnrich() {
        const { sessionId, chosenCandidateId, editedFields } = get();
        if (!sessionId || !chosenCandidateId) return;
        retryFrom = { turn: 'enrich' };
        set({ flow: { step: 'enriching' } });
        beginTurn();
        const result = await deps.service.enrich(sessionId, {
          candidateId: chosenCandidateId,
          ...(Object.keys(editedFields).length > 0 ? { confirmedFields: editedFields } : {}),
        });
        endTurn();
        if (!result.ok) { fail(result.error); return; }

        const { fields, logo, categoryGuesses, stagesFailed } = result.data;
        set({
          flow: { step: 'reviewing', fields, logo, categoryGuesses, stagesFailed },
          // FR-W8: the top guess arrives pre-selected.
          selectedCategories: categoryGuesses.length > 0 ? [categoryGuesses[0].category] : [],
        });
      },

      toggleCategory(category: StashCategory) {
        const selected = get().selectedCategories;
        const exists = selected.some((entry) => entry.recordID === category.recordID);
        set({
          selectedCategories: exists
            ? selected.filter((entry) => entry.recordID !== category.recordID)
            : [...selected, category],
        });
      },

      async save() {
        const state = get();
        const flow = state.flow;
        if (flow.step !== 'reviewing' || !state.sessionId || !state.chosenCandidateId) return;
        if (state.selectedCategories.length === 0) return; // FR-W8 guard

        const vendor: Record<string, unknown> = {};
        for (const field of flow.fields) vendor[field.field] = field.value;
        Object.assign(vendor, state.editedFields);
        vendor.categories = state.selectedCategories;

        await runCommit(state.sessionId, {
          candidateId: state.chosenCandidateId,
          vendor,
          resolution: null,
          matchedVendorId: null,
        });
      },

      async resolveMatch(resolution) {
        const state = get();
        const flow = state.flow;
        if (flow.step !== 'resolving' || !state.sessionId || !state.chosenCandidateId) return;

        const vendor: Record<string, unknown> = { ...state.editedFields };
        // Rebuild the authoritative payload the same way save() did.
        vendor.categories = state.selectedCategories;
        await runCommit(state.sessionId, {
          candidateId: state.chosenCandidateId,
          vendor: lastVendorPayload ?? vendor,
          resolution,
          matchedVendorId: resolution === 'useExisting' ? flow.match.vendorId : null,
        });
      },

      async retry() {
        const pending = retryFrom;
        if (!pending) return;
        if (pending.turn === 'create') { await get().startWithFile(pending.file); return; }
        if (pending.turn === 'enrich') {
          // Re-enter from the confirming state we still hold.
          await get().confirmAndEnrich();
          return;
        }
        // Same commit request (and the service's per-session idempotency key),
        // so a retry after a partial failure can only replay, never double-write.
        await runCommit(pending.sessionId, pending.request);
      },

      async resumeFromSession(sessionId) {
        beginTurn();
        const result = await deps.service.getSnapshot(sessionId);
        endTurn();
        if (!result.ok) { fail(result.error); return; }
        const snapshot = result.data;
        set({ sessionId: snapshot.sessionId, expiresAt: snapshot.expiresAt, restartNotice: null });

        switch (snapshot.status) {
          case 'extracted': {
            const selected = snapshot.selectedCandidateId
              ? snapshot.candidates.find((entry) => entry.candidateId === snapshot.selectedCandidateId)
              : undefined;
            if (selected) {
              set({
                chosenCandidateId: selected.candidateId,
                flow: { step: 'confirming', candidate: selected },
              });
            } else {
              set({
                flow: {
                  step: 'choosing', candidates: snapshot.candidates,
                  observed: null, sourceType: snapshot.sourceType,
                },
              });
            }
            return;
          }
          case 'enriched':
            set({
              chosenCandidateId: snapshot.selectedCandidateId,
              flow: {
                step: 'reviewing',
                fields: snapshot.fields ?? [],
                logo: null, // snapshot does not carry the logo asset
                categoryGuesses: snapshot.categoryGuesses ?? [],
                stagesFailed: [],
              },
              selectedCategories: (snapshot.categoryGuesses?.length ?? 0) > 0
                ? [snapshot.categoryGuesses![0].category]
                : [],
            });
            return;
          case 'committed': {
            const committed = snapshot.committedResult;
            if (committed) {
              set({
                flow: {
                  step: 'done', vendorId: committed.vendorId,
                  stashCardId: committed.stashCardId, createdVendor: committed.createdVendor,
                },
              });
              return;
            }
            break;
          }
          default:
            break;
        }
        // expired / error / unrecoverable → restart with notice
        get().reset();
        set({ restartNotice: EXPIRED_NOTICE });
      },

      reset() {
        clearTimeout(slowTimer);
        retryFrom = null;
        lastVendorPayload = null;
        set({
          flow: { step: 'idle' },
          sessionId: null,
          expiresAt: null,
          chosenCandidateId: null,
          editedFields: {},
          selectedCategories: [],
          photoPreviewUrl: null,
          slowTurn: false,
          restartNotice: null,
        });
      },
    };

    /** Shared by save() and resolveMatch(): one commit turn with all outcomes. */
    async function runCommit(
      sessionId: string,
      request: CommitTurnRequest
    ): Promise<void> {
      lastVendorPayload = request.vendor as Record<string, unknown>;
      retryFrom = { turn: 'commit', sessionId, request };
      set({ flow: { step: 'committing' } });
      beginTurn();
      const result = await deps.service.commit(sessionId, request);
      endTurn();
      if (!result.ok) { fail(result.error); return; }

      const outcome = result.data;
      if (outcome.status === 'matchFound') {
        set({ flow: { step: 'resolving', match: outcome.match } });
        return;
      }
      set({
        flow: {
          step: 'done', vendorId: outcome.vendorId,
          stashCardId: outcome.stashCardId, createdVendor: outcome.createdVendor,
        },
      });
    }
  });
}

export type SnapStashStoreApi = ReturnType<typeof createSnapStashStore>;
